import type { Disposable } from '@/shared/types/core'
import { normalizeUnitPreference, type UnitPreference } from '@/shared/unitPresentation'

/** 单位偏好变化监听器。 */
export type UnitPreferenceChangeListener = (
  preference: Readonly<UnitPreference>,
  previous: Readonly<UnitPreference>
) => void

/** 引擎实例级显示单位服务。 */
export class UnitPresentationService implements Disposable {
  private _preference: Readonly<UnitPreference>
  private readonly listeners = new Set<UnitPreferenceChangeListener>()

  constructor(preference?: Partial<UnitPreference> | null) {
    this._preference = Object.freeze(normalizeUnitPreference(preference))
  }

  /** 当前规范化后的单位偏好。 */
  get preference(): Readonly<UnitPreference> {
    return this._preference
  }

  /**
   * 更新实例单位偏好，仅在实际变化时通知订阅者。
   *
   * @param preference - 新偏好，可传部分字段
   */
  setPreference(preference: Partial<UnitPreference>): void {
    const next = normalizeUnitPreference({
      ...this._preference,
      ...preference,
    })
    if (
      next.displayUnit === this._preference.displayUnit &&
      next.feetFormat === this._preference.feetFormat
    ) {
      return
    }

    const previous = this._preference
    this._preference = Object.freeze(next)
    for (const listener of this.listeners) {
      listener(this._preference, previous)
    }
  }

  /**
   * 订阅单位偏好变化。
   *
   * @param listener - 变化监听器
   * @returns 取消订阅函数
   */
  subscribe(listener: UnitPreferenceChangeListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** 清空所有监听器。 */
  dispose(): void {
    this.listeners.clear()
  }
}
